import React from 'react'
import Badge from '../Badge/Badge.component'
import type {
  DescriptionListItem,
  DescriptionListProps,
} from './DescriptionList.types'
import {
  DESCRIPTION_LIST_CLAIMS_GROUPS,
  DESCRIPTION_LIST_DEMO_ITEMS,
  DESCRIPTION_LIST_FILE_DETAILS_ITEMS,
} from './DescriptionList.types'
import './DescriptionList.css'

function chunkItems(
  items: DescriptionListItem[],
  columns: number,
): DescriptionListItem[][] {
  if (columns <= 1 || items.length === 0) {
    return [items]
  }

  const size = Math.ceil(items.length / columns)
  const groups: DescriptionListItem[][] = []

  for (let index = 0; index < columns; index += 1) {
    const start = index * size
    const slice = items.slice(start, start + size)
    if (slice.length > 0) {
      groups.push(slice)
    }
  }

  return groups
}

const DescriptionList: React.FC<DescriptionListProps> = ({
  items,
  groups,
  columns = 3,
  variant = 'panel',
  className = '',
  darkMode = false,
  previewState,
}) => {
  const isPreview = previewState != null

  const displayVariant =
    previewState === 'plain'
      ? 'plain'
      : previewState === 'file-details'
        ? 'file-details'
        : variant

  const previewColumns =
    previewState === 'one-column' || previewState === 'file-details'
      ? 1
      : previewState === 'two-column' ||
          previewState === 'with-badge' ||
          previewState === 'plain'
        ? 2
        : previewState === 'claims-sample' || previewState === 'default'
          ? 3
          : columns

  const resolvedColumns =
    displayVariant === 'file-details' && !isPreview
      ? Math.min(columns, 1)
      : columns

  const resolvedGroups = ((): DescriptionListItem[][] => {
    if (groups != null && groups.length > 0) {
      return groups
    }

    if (items != null && items.length > 0) {
      const colCount =
        displayVariant === 'file-details'
          ? 1
          : isPreview
            ? previewColumns
            : resolvedColumns
      return chunkItems(items, colCount)
    }

    if (!isPreview) {
      return []
    }

    switch (previewState) {
      case 'claims-sample':
      case 'default':
        return DESCRIPTION_LIST_CLAIMS_GROUPS
      case 'file-details':
        return [DESCRIPTION_LIST_FILE_DETAILS_ITEMS]
      case 'with-badge':
        return chunkItems(
          [
            { label: 'Claim ID', value: 'CLM-10482' },
            {
              label: 'Claim status',
              value: <Badge label="Posted" mode="tinted" color="vivid-green" />,
            },
            { label: 'Claim amount', value: '$1,250.00' },
            {
              label: 'Check status',
              value: <Badge label="Deposited" mode="tinted" color="teal" />,
            },
            { label: 'Paid amount', value: '$980.00' },
            { label: 'Payment date', value: '03/18/2026' },
          ],
          2,
        )
      case 'one-column':
        return chunkItems(DESCRIPTION_LIST_DEMO_ITEMS.slice(0, 5), 1)
      case 'two-column':
        return chunkItems(DESCRIPTION_LIST_DEMO_ITEMS.slice(0, 8), 2)
      case 'plain':
        return chunkItems(DESCRIPTION_LIST_DEMO_ITEMS.slice(0, 6), 2)
      default:
        return DESCRIPTION_LIST_CLAIMS_GROUPS
    }
  })()

  const columnCount = Math.min(Math.max(resolvedGroups.length, 1), 3)
  const showConnectors = displayVariant === 'file-details'

  const classNames = [
    'ds-description-list',
    `ds-description-list--${displayVariant}`,
    `ds-description-list--cols-${columnCount}`,
    darkMode ? 'ds-description-list--dark' : '',
    isPreview ? 'ds-description-list--preview' : '',
    previewState ? `ds-description-list--preview-${previewState}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classNames}>
      {resolvedGroups.map((columnItems, columnIndex) => (
        <dl key={columnIndex} className="ds-description-list__column">
          {columnItems.map((item, rowIndex) => (
            <div
              key={`${columnIndex}-${rowIndex}`}
              className="ds-description-list__row"
            >
              <dt className="ds-description-list__label">{item.label}</dt>
              {showConnectors ? (
                <div className="ds-description-list__connector" aria-hidden />
              ) : null}
              <dd className="ds-description-list__value">{item.value}</dd>
            </div>
          ))}
        </dl>
      ))}
    </div>
  )
}

export default DescriptionList
